import { A } from "@solidjs/router";

const AboutScheme = () => (
	<div class="rias-content col-span-full">
		<h1 class="headline-lg">About the Scheme</h1>

		<h2>Background</h2>

		<p>
			The Building (Scotland) Act 2003 provides the opportunity for design professionals
			practising in Scotland to be able to certify that their design complies with the
			guidance in one or more Sections of the Technical Standards. Certification comes with
			responsibility and all Approved Certifiers of Design must demonstrate their competency
			with the appropriate Section of the Technical Standards. Issuing a Certificate of Design
			means that the Warrant application fee can be reduced, and the local authority does not
			need to scrutinise compliance with that particular Section of the Technical Standards.
		</p>

		<p>
			Only Approved Certifiers of Design who have gained membership of a Certification Scheme
			approved by Scottish Ministers can issue Certificates of Design.
		</p>

		<p>
			Once approved an Approved Certifier of Design can certify compliance of either their own
			or others designs for conversions, alterations, extensions and new buildings, based on
			their competency.
		</p>

		<p>
			The RIAS received Ministerial approval on 21<sup>st</sup> August 2007 to provide a
			Section 6 &#8211; Energy (Domestic) Certification Scheme and has been actively accepting
			applications and approving Certifiers of Design (Section 6 &#8211; Energy) Domestic
			since 1st October 2007. This Scheme was joined on 28th November 2013 by one to approve
			Certifiers of Design (Section 6 Non-domestic) Energy.
		</p>

		<p>
			Where provided, Section 6 &#8211; Energy Certificates must be accepted by local
			authorities as part of a Building Warrant application. They guarantee that the design as
			presented is compliant with the guidance in Section 6 &#8211; Energy of the Technical
			Standards.
		</p>

		<h2>Applying for Membership</h2>

		<p>
			The Domestic and Non-domestic (Section 6 &#8211; Energy) Schemes are available to all
			competent and experienced construction professionals. All applicants to the Schemes are
			required to complete a series of competency tests to ensure a minimum threshold of
			expertise is met by all.
		</p>

		<p>Membership has four stages:</p>
		<ol>
			<li>Initial application</li>
			<li>On-line competency tests</li>
			<li>Practical calculation test / confirmation of prior competency</li>
			<li>Approved Body checks</li>
		</ol>

		<p>On initial application many applicants will pre-qualify. These are:</p>
		<ol>
			<li>Architects registered with the Architects Registration Board (ARB)</li>
			<li>
				Chartered Members of the Chartered Institute of Architectural Technologists
				(MCIAT/FCIAT)
			</li>
			<li>
				Corporate Members of and Low Carbon Consultants (LCCs) registered with, the
				Chartered Institute of Building Services Engineers (CIBSE)
			</li>
			<li>
				Existing Approved Certifiers of Design, whether they are moving Scheme or adding an
				additional Scheme
			</li>
		</ol>

		<p>
			For those who do not pre-qualify they will be asked to provide evidence that they have:
		</p>
		<ol>
			<li>
				A suitable construction related qualification at NVQ / SVQ level IV, such as a HND or
				above
			</li>
			<li>
				A minimum of 5 years of experience in the design of either domestic or non-domestic
				buildings (depending on the Scheme applied for)
			</li>
			<li>
				Evidence of specific training in a relevant energy calculation methodology (SAP,
				SBEM or PHPP) or demonstration that at least 2 building warrants have been granted
				based on the applicant’s energy calculations (SAP or iSBEM etc).
			</li>
		</ol>

		<h2>On-line Tests</h2>
		<p>
			Once an application has been accepted, the applicant is provided with the Scheme
			Guidance, a series of Learning Modules and relevant information and must then complete a
			series of six on-line tests, to demonstrate a familiarity and understanding of the
			process of certification, the relevant Sections of the Technical Standards and the
			documents that support them, such as the Domestic and Non-domestic Building Services
			Compliance Guides for Scotland.
		</p>

		<h2>Practical Calculation Test</h2>
		<p>
			At the heart of demonstrating compliance for new buildings is the ability to accurately
			calculate the energy performance of a building. Applicants to the Domestic Scheme will
			be required to complete a SAP calculation based on a test building provided by the
			Scheme, using one of the Scottish Government approved domestic software packages which
			are available. Applicants to the Non-domestic Scheme who have previous training in using
			one of the Scottish Government approved non-domestic software packages will be asked to
			provide evidence of this. Alternatively, RIAS-regs have twinned with Strathclyde
			University who have prepared a series of on-line tutorials and a practical calculation
			test using iSBEM.
		</p>
		<p>
			Further details of these can be found at:{" "}
			<a href="https://onlineshop.strath.ac.uk/product-catalogue/engineering-faculty/mechanical-aerospace-engineering/courses/riascibse-certifier-of-design-training">
				https://onlineshop.strath.ac.uk/product-catalogue/engineering-faculty/mechanical-aerospace-engineering/courses/riascibse-certifier-of-design-training
			</a>
		</p>

		<h2>Approved Bodies</h2>
		<p>
			Once an applicant has demonstrated competency they can become an Approved Certifier of
			Design once they have provided details of their Approved Body. The Approved Body is the
			legal entity which issues the Certificates of Design created by the Approved Certifier of
			Design. Usually this will be the Approved Certifier of Design&#8217;s practice or
			employer. Where an Approved Certifier of Design is a sole practitioner they are also
			their own Approved Body.
		</p>
		<p>
			The Approved Body must provide a copy of their Professional Indemnity Insurance (PII)
			and confirm that they will support the Approved Certifier of Design by providing them
			with the resources they need.
		</p>

		<h2>Energy Performance Certificates</h2>
		<p>
			Approved Certifiers of Design (Section 6 – Energy) who wish to lodge EPCs with the
			Scottish EPC Register, must become a Member of an Approved Organisation. A full list of
			those who do, can be found at:{" "}
			<a href="https://www.gov.scot/Topics/Built-Environment/Building/Building-standards/enerperfor/epcorgprg">
				https://www.gov.scot/Topics/Built-Environment/Building/Building-standards/enerperfor/epcorgprg
			</a>
		</p>
		<p>
			Approved Certifiers of Design (Domestic) can opt in to the Scheme provided by Quidos,
			without further training and lodge EPCs for new domestic buildings. For details of the
			Quidos Scheme follow the link to:{" "}
			<a href="https://quidos.co.uk/">https://quidos.co.uk/</a>
		</p>
		<p>
			Approved Certifiers of Design (Non-domestic) can opt in to be a CIBSE Low Carbon
			Consultant (if not already registered) and lodge EPCs for new non-domestic buildings
			with the Scottish EPC Register. For details of the CIBSE Scheme follow the link to:{" "}
			<a href="https://www.cibsecertification.co.uk/personnel-certification-schemes/schemes/low-carbon-energy-assessors-to-produce-non-domestic-epcs">
				https://www.cibsecertification.co.uk/personnel-certification-schemes/schemes/low-carbon-energy-assessors-to-produce-non-domestic-epcs
			</a>
		</p>

		<h2>Scheme Benefits</h2>
		<ol>
			<li>
				As an Approved Certifier of Design, you are able to retain control of the energy
				aspects of your projects, provide clients with an enhanced service or help design
				teams optimise their energy strategies
			</li>
			<li>
				In an increasingly complex regulatory environment, certification minimises the time
				taken by local authorities to verify applications
			</li>
			<li>
				Where the intention is to lodge a Certificate of Design for Section 6, the warrant
				fee can be discounted by at least 10% for each Section of the Technical Standards
				which is certified. Where a project includes both Domestic and Non-domestic
				elements, both require to be certified
			</li>
			<li>
				Certificates of Design can be issued for all forms of construction, included
				alterations &amp; extensions, conversions and new builds
			</li>
			<li>
				On completion of new buildings Approved Certifiers of Design who have joined an
				Approved Organisation can lodge Energy Performance Certificates
			</li>
			<li>
				Approved Certifiers of Design can join multiple Schemes provided by{" "}
				<A href="https://rias-regs.co.uk/join">RIAS-regs.co.uk</A>
			</li>
			<li>
				Approved Certifiers of Design will be kept up to date with the latest changes to the
				Technical Standards and will have access to Technical Advice.
			</li>
		</ol>

		<p>
			The first step to becoming Approved Certifier of Design of Section 6 (Domestic) is to
			register your interest online by using the Register Now link.
		</p>
	</div>
);

export default AboutScheme;
